import * as React from "react";
import { NavLink } from "react-router-dom";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import useTheme from "@mui/material/styles/useTheme";

import AssignedTagsList from "components/assigned-tags";
import PaymentSourceIcon from "components/payment-source-icon";
import CallPatientLink from "components/suno-dialer/call-patient/call-patient-link.component";
import { PAYMENT_SOURCE_TYPE } from "constants/patients";
import { TagTypes } from "constants/tags";
import { WebPatientData } from "services/types/appointments.type";
import type { AssignedTagResponse } from "services/types/assigned-tag.type";
import type { Patient } from "services/types/patients.type";
import { formatDateOnlyValue, formatPhoneNumber } from "utils/common";

import { classes } from "../appointment-preview.component";

type AppointmentPatientProps = {
  patient: Patient | WebPatientData;
  assignedTags?: AssignedTagResponse;
  showPatientTagsTooltip?: boolean;
};
export default function AppointmentPatient({
  patient,
  assignedTags,
  showPatientTagsTooltip
}: AppointmentPatientProps) {
  const { shadows } = useTheme();
  const birthdate = patient?.birthdate
    ? formatDateOnlyValue(String(patient.birthdate))
    : "";

  const phone = patient?.phone
    ? formatPhoneNumber(patient?.phone, "parenthesis")
    : "";

  return (
    <>
      <Grid container direction="row">
        <Grid item mr={2}>
          {patient && "id" in patient ? (
            <NavLink
              to={`/patients/${patient.id}`}
              className={classes.text}
              data-testid="appointment-preview-patient"
            >
              <Box component="span" sx={{ fontWeight: 700, fontSize: "16px" }}>
                {patient.fullName}
              </Box>
            </NavLink>
          ) : (
            <Typography
              component="div"
              data-testid="appointment-preview-patient"
            >
              {patient.first_name} {patient.last_name} (from web)
            </Typography>
          )}
          {Boolean(birthdate) && (
            <Typography
              component="span"
              sx={{ color: grey[800], pl: 1, fontSize: "13px" }}
            >
              {birthdate}
            </Typography>
          )}
        </Grid>
        <Grid item>
          {"paymentSourceType" in patient && patient.paymentSourceType && (
            <Stack flexDirection="row">
              <PaymentSourceIcon
                className={classes.grayIcon}
                style={{
                  marginLeft: 1,
                  maxWidth: 18,
                  maxHeight: 18,
                  marginRight: 4,
                  paddingTop: 1
                }}
                paymentSource={patient.paymentSourceType}
              />
              <Typography component="div" sx={{ lineHeight: 1.4 }}>
                {PAYMENT_SOURCE_TYPE.getFromCode(patient.paymentSourceType)}
              </Typography>
            </Stack>
          )}
        </Grid>
      </Grid>
      {Boolean(phone) && (
        <Grid container>
          <Grid item mr={2} mt={0.25}>
            <CallPatientLink
              label={
                <Typography
                  component="span"
                  sx={{ color: grey[800], fontSize: "13px", pr: 1 }}
                >
                  Phone:
                </Typography>
              }
              patient={patient}
              phoneNumber={phone}
              containerSx={{ mt: -1 }}
            >
              <Typography
                component="span"
                sx={{ color: grey[800], fontSize: "13px" }}
              >
                Phone: {phone}
              </Typography>
            </CallPatientLink>
          </Grid>
        </Grid>
      )}
      {patient && "id" in patient && (
        <Grid container>
          <Grid item mr={2} mt={0.25}>
            <Typography
              component="span"
              sx={{ color: grey[800], fontSize: "13px", mt: -1 }}
            >
              Suno ID: {patient.id}
            </Typography>
          </Grid>
        </Grid>
      )}

      {patient &&
        "primary_insurance_policy" in patient &&
        patient.primary_insurance_policy?.insurer?.name && (
          <Grid container>
            <Grid item mr={2} mt={0.25}>
              <Typography
                component="span"
                sx={{ color: grey[800], fontSize: "13px", mt: -1 }}
              >
                Primary Insurance:{" "}
                {patient.primary_insurance_policy.insurer?.name ?? "Unverified"}
              </Typography>
            </Grid>
          </Grid>
        )}

      {patient && "noah_id" in patient && (
        <Grid container>
          <Grid item mr={2} mt={0.25}>
            <Typography
              component="span"
              sx={{ color: grey[800], fontSize: "13px", mt: -1 }}
            >
              Noah ID: {patient.noah_id}
            </Typography>
          </Grid>
        </Grid>
      )}
      {Boolean(assignedTags?.length) && (
        <Grid container>
          <Grid item mr={2} mt={-1} position="relative">
            <Box zIndex={1}>
              <AssignedTagsList
                tagType={TagTypes.PATIENT}
                tags={assignedTags}
                dotsSize="10px"
                showDotsOnly
                sx={{ flexDirection: "column" }}
              />
            </Box>
            {showPatientTagsTooltip && (
              <Box
                sx={{
                  border: `solid 1px ${grey[300]}`,
                  background: grey[100],
                  boxShadow: shadows[2],
                  position: "absolute",
                  pointerEvents: "none",
                  left: "-4px",
                  top: "24px",
                  zIndex: 2,
                  p: 1,
                  borderRadius: "4px",
                  maxWidth: "562px",
                  minWidth: "380px"
                }}
              >
                <Stack rowGap={0.5}>
                  <Typography variant="body1">Patient&nbsp;Tags:</Typography>
                  <AssignedTagsList
                    tagType={TagTypes.PATIENT}
                    tags={assignedTags}
                    sx={{ flexDirection: "column" }}
                  />
                </Stack>
              </Box>
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
}
